import { useEffect, useState } from 'react'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { Link } from 'react-router-dom'
import { AlertTriangle, Clock, Package, ShoppingCart, Truck, FileText, Bell } from 'lucide-react'
import { format, isPast, addDays, differenceInDays } from 'date-fns'

export default function Dashboard() {
  const { activeBrand } = useAuth()
  const [stats, setStats] = useState({ products: 0, suppliers: 0, openPOs: 0, pendingShipments: 0, unpaidInvoices: 0, unpaidAmount: 0 })
  const [reorderAlerts, setReorderAlerts] = useState([])
  const [inventoryAlerts, setInventoryAlerts] = useState([])
  const [expiringLots, setExpiringLots] = useState([])
  const [recentPOs, setRecentPOs] = useState([])
  const [lastLotImport, setLastLotImport] = useState(undefined)
  const [lotAlertDismissed, setLotAlertDismissed] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (activeBrand) load()
  }, [activeBrand])

  async function load() {
    setLoading(true)
    const bid = activeBrand.id

    const [
      { count: products },
      { count: suppliers },
      { count: openPOs },
      { count: pendingShipments },
      { data: invoiceData },
      { data: pos },
      { data: lots },
      { data: onHand },
      { data: lotImportLog }
    ] = await Promise.all([
      supabase.from('products').select('*', { count: 'exact', head: true }).eq('brand_id', bid).eq('is_active', true),
      supabase.from('suppliers').select('*', { count: 'exact', head: true }).eq('brand_id', bid),
      supabase.from('purchase_orders').select('*', { count: 'exact', head: true }).eq('brand_id', bid).in('status', ['draft', 'sent', 'confirmed', 'partially_received']),
      supabase.from('shipments').select('*', { count: 'exact', head: true }).eq('brand_id', bid).in('status', ['in_transit', 'arrived', 'receiving']),
      supabase.from('invoices').select('amount, amount_paid').eq('brand_id', bid).in('status', ['unpaid', 'partially_paid', 'overdue']),
      supabase.from('purchase_orders').select('id, po_number, status, order_date, suppliers(name)').eq('brand_id', bid).order('created_at', { ascending: false }).limit(5),
      supabase.from('shipment_line_items').select('lot_number, expiration_date, quantity_received, products(name, sku), shipments!inner(brand_id, is_direct_ship)').eq('shipments.brand_id', bid).eq('shipments.is_direct_ship', false).not('expiration_date', 'is', null).lte('expiration_date', format(addDays(new Date(), 90), 'yyyy-MM-dd')).order('expiration_date'),
      supabase.from('inventory_on_hand').select('product_id, on_hand').eq('brand_id', bid),
      supabase.from('lot_import_log').select('imported_at').eq('brand_id', bid).order('imported_at', { ascending: false }).limit(1)
    ])

    const unpaidAmount = (invoiceData || []).reduce((sum, i) => sum + (i.amount - i.amount_paid), 0)

    setLastLotImport(lotImportLog?.[0]?.imported_at ?? null)
    setLotAlertDismissed(false)
    setStats({ products, suppliers, openPOs, pendingShipments, unpaidInvoices: (invoiceData || []).length, unpaidAmount })
    setRecentPOs(pos || [])
    const productsWithInventory = new Set(
      (onHand || []).filter(r => Number(r.on_hand) > 0).map(r => r.product_id)
    )
    const filteredLots = (lots || []).filter(l => productsWithInventory.has(l.products?.id) || productsWithInventory.size === 0)
    setExpiringLots(filteredLots.slice(0, 5))
    setLoading(false)
  }

  const poStatusBadge = (s) => {
    const map = { draft: 'neutral', sent: 'info', confirmed: 'accent', partially_received: 'warning', received: 'success', cancelled: 'danger' }
    return map[s] || 'neutral'
  }

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>{activeBrand?.name}</h1>
          <p style={{ color: 'var(--text-3)', fontSize: '13px', marginTop: '2px' }}>Overview · {format(new Date(), 'MMMM d, yyyy')}</p>
        </div>
      </div>

      {/* Lot import staleness alert */}
      {!lotAlertDismissed && lastLotImport !== undefined && (lastLotImport === null || differenceInDays(new Date(), new Date(lastLotImport)) > 7) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'var(--warning-bg)', border: '1px solid rgba(240,160,80,0.3)', borderRadius: 'var(--radius)', marginBottom: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <AlertTriangle size={15} style={{ color: 'var(--warning)', flexShrink: 0 }} />
            <span style={{ fontSize: '13px', color: 'var(--text)' }}>
              {lastLotImport === null
                ? 'No lot data has been imported yet.'
                : `Lot data is ${differenceInDays(new Date(), new Date(lastLotImport))} days old — import due.`}
              {' '}
              <Link to="/lot-import" style={{ color: 'var(--accent)', textDecoration: 'none', fontWeight: 500 }}>Import now →</Link>
            </span>
          </div>
          <button onClick={() => setLotAlertDismissed(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-3)', fontSize: '16px', lineHeight: 1, padding: '0 2px' }}>×</button>
        </div>
      )}

      {/* Stats */}
      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-label">Active SKUs</div>
          <div className="stat-value">{stats.products}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Suppliers</div>
          <div className="stat-value">{stats.suppliers}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Open POs</div>
          <div className="stat-value">{stats.openPOs}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Inbound Shipments</div>
          <div className="stat-value">{stats.pendingShipments}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Unpaid Invoices</div>
          <div className="stat-value">{stats.unpaidInvoices}</div>
          <div className="stat-sub">${stats.unpaidAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })} outstanding</div>
        </div>
      </div>

      {/* Inventory Alerts */}
      {inventoryAlerts.length > 0 && (
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <Bell size={16} style={{ color: 'var(--danger)' }} />
            <h3 style={{ fontSize: '14px', fontWeight: 600 }}>Inventory Alerts ({inventoryAlerts.length})</h3>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {inventoryAlerts.map((alert, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', background: alert.severity === 'danger' ? 'var(--danger-bg)' : 'var(--warning-bg)', borderRadius: 'var(--radius)', border: `1px solid ${alert.severity === 'danger' ? 'rgba(224,92,106,0.2)' : 'rgba(240,160,80,0.2)'}` }}>
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>{alert.product.sku} — {alert.product.name}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-2)', marginTop: '2px' }}>
                    {alert.type === 'reorder'
                      ? `${alert.totalSupplyDays}d total supply · Lead time ${alert.leadTime}d · Order ${alert.suggestedQty.toLocaleString()} units`
                      : `FBA at ${alert.fbaDays}d · Target ${alert.fbaTarget}d · Transfer ${alert.suggestedQty.toLocaleString()} units`}
                  </div>
                </div>
                <span className={`badge badge-${alert.severity}`} style={{ flexShrink: 0 }}>
                  {alert.type === 'reorder' ? '🔴 Reorder' : '📦 FBA Transfer'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        {/* Expiring Lots */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <Clock size={16} style={{ color: 'var(--warning)' }} />
            <h3 style={{ fontSize: '14px', fontWeight: '600' }}>Expiring Within 90 Days</h3>
          </div>
          {expiringLots.length === 0 ? (
            <div style={{ color: 'var(--text-3)', fontSize: '13px' }}>No lots expiring soon.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {expiringLots.map((lot, i) => {
                const exp = new Date(lot.expiration_date)
                const isExpired = isPast(exp)
                const daysLeft = Math.ceil((exp - new Date()) / (1000 * 60 * 60 * 24))
                return (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <div style={{ fontSize: '13px', fontWeight: '500' }}>{lot.products?.name}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-3)', fontFamily: 'var(--mono)' }}>Lot: {lot.lot_number}</div>
                    </div>
                    <span className={`badge ${isExpired ? 'badge-danger' : daysLeft <= 30 ? 'badge-warning' : 'badge-info'}`}>
                      {isExpired ? 'Expired' : `${daysLeft}d left`}
                    </span>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {/* Recent POs */}
        <div className="card">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ShoppingCart size={16} style={{ color: 'var(--accent)' }} />
              <h3 style={{ fontSize: '14px', fontWeight: '600' }}>Recent Purchase Orders</h3>
            </div>
            <Link to="/purchase-orders" style={{ fontSize: '12px', color: 'var(--accent)', textDecoration: 'none' }}>View all →</Link>
          </div>
          {recentPOs.length === 0 ? (
            <div style={{ color: 'var(--text-3)', fontSize: '13px' }}>No purchase orders yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {recentPOs.map(po => (
                <Link key={po.id} to={`/purchase-orders/${po.id}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', textDecoration: 'none' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text)' }}>{po.po_number}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-3)' }}>{po.suppliers?.name}</div>
                  </div>
                  <span className={`badge badge-${poStatusBadge(po.status)}`}>{po.status.replace('_', ' ')}</span>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
